export const isEmpty = (value: any) => value === null || value === undefined || value === '';

export type ValidationResult = undefined | { key: string; values?: Record<string, string | number> };

const predefinedFormats: Record<string, RegExp> = {
  default:
    /^[a-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u00FF\u0100-\u024F\d\-./,()&+:!? '"\u2018-\u201F\u0600-\u06FF\u0590-\u05FF\u0400-\u04FF]+$/i,
  email:
    // eslint-disable-next-line no-control-regex, no-useless-escape
    /^((([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))$/i,
  name: /^[a-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u00FF\u0100-\u024F\-. \u0600-\u06FF\u0590-\u05FF\u0400-\u04FF]+$/i,
  streetOrCity:
    /^[a-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u00FF\u0100-\u024F\-. '\u2018-\u2019\u0600-\u06FF\u0590-\u05FF\u0400-\u04FF]+$/i,
  iban: /^[A-Z]{2}\d{2}(?:[ ]?\d{4}){4}(?!(?:[ ]?\d){3})(?:[ ]?\d{1,2})?$/,
};

const getFormat = (format: RegExp | keyof typeof predefinedFormats | string) => {
  if (typeof format !== 'string') {
    return format;
  } else if (format in predefinedFormats) {
    return predefinedFormats[format];
  } else {
    return new RegExp(format);
  }
};

export type Validation = {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  format?: RegExp | keyof typeof predefinedFormats | string;
  illegalTerms?: string[];
};

const getValidator =
  ({ required, minLength, format, illegalTerms }: Omit<Validation, 'maxLength'>) =>
  (value: string): ValidationResult => {
    const trimmedValue = typeof value === 'string' ? value.trim() : value;
    if (!required && isEmpty(trimmedValue)) {
      return undefined;
    } else if (isEmpty(trimmedValue)) {
      return { key: 'required' };
    } else if (minLength && trimmedValue.length < minLength) {
      return { key: 'minLength', values: { minLength } };
    } else if (format && !getFormat(format).test(value)) {
      return { key: 'invalid' };
    } else if (illegalTerms?.some(term => value.toLowerCase().includes(term.toLowerCase()))) {
      return { key: 'invalid' };
    } else {
      return undefined;
    }
  };

export default getValidator;
